import { useEffect, useRef, useState } from 'react';
import { Printer, ListPlus, X } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { A4PrintLayout, ThermalPrintLayout } from '../../../shared/inventory/DeliveryLabelLayouts';
import { warehouseApi as api, apiError } from '../shared/warehouseApi';

export function deliveryKey(pkg) {
  if (pkg.delivery_point_id) return `lima:${pkg.delivery_point_id}`;
  if (pkg.delivery_point_provincia_id) return `provincia:${pkg.delivery_point_provincia_id}`;
  return null;
}

export function togglePrintQueue(queue, pkg) {
  const key = deliveryKey(pkg);
  if (!key) return queue;
  return queue.some(p => deliveryKey(p) === key) ? queue.filter(p => deliveryKey(p) !== key) : [...queue, pkg].slice(0, 100);
}

export function PrintActions({ pkg, queued, onQueue, onPrint }) {
  const supported = Boolean(deliveryKey(pkg) && pkg.tracking_code);
  return <>
    <button type="button" disabled={!supported} title={supported ? 'Imprimir entrega' : 'Entrega sin etiqueta compatible'} aria-label="Imprimir entrega" onClick={() => onPrint(pkg)} className="p-2 rounded hover:bg-gray-100 disabled:opacity-30"><Printer size={16} /></button>
    <button type="button" disabled={!supported} title={queued ? 'Quitar de cola' : 'Agregar a cola'} aria-label={queued ? 'Quitar de cola' : 'Agregar a cola'} aria-pressed={queued} onClick={() => onQueue(pkg)} className={`p-2 rounded ${queued ? 'bg-brand-primary text-white' : 'hover:bg-gray-100'} disabled:opacity-30`}><ListPlus size={16} /></button>
  </>;
}

export function PrintQueue({ queue, setQueue, immediate, onImmediateHandled }) {
  const [format, setFormat] = useState('a4');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [job, setJob] = useState(null);
  const ref = useRef();
  const launched = useRef(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const print = useReactToPrint({ content: () => ref.current,
    pageStyle: format === 'thermal' ? '@page { size: 80mm auto; margin: 0; }' : '@page { size: A4 portrait; margin: 0; }',
    onAfterPrint: () => setBusy(false), onPrintError: () => { setError('No se pudo abrir la impresión. La cola se conservó.'); setBusy(false); } });
  useEffect(() => {
    if (job && launched.current !== job) { launched.current = job; print(); }
  }, [job, print]);
  async function prepare(entries) {
    if (!entries.length || busy) return;
    setBusy(true); setError('');
    try {
      const ids = entries.map(p => p.id);
      const { data } = await api.post('/api/admin/packages/print-data', { package_ids: ids });
      if (data.missing_package_ids?.length) throw new Error(`Sin etiqueta: ${data.missing_package_ids.join(', ')}. Revise la cola.`);
      if (!data.items?.length) throw new Error('No hay etiquetas disponibles.');
      await api.post('/api/admin/packages/print-request', { package_ids: ids, format });
      if (mounted.current) setJob(data.items);
    } catch (err) {
      if (mounted.current) { setError(err.response ? apiError(err) : err.message); setBusy(false); }
    }
  }
  const handled = useRef(null);
  useEffect(() => {
    if (immediate && handled.current !== immediate) {
      handled.current = immediate;
      prepare([immediate]); onImmediateHandled();
    }
    // Only the user's explicit immediate-print request launches this operation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate]);
  const lima = job?.filter(d => d.type === 'lima') || [];
  const provincia = job?.filter(d => d.type === 'provincia') || [];
  return <section className="bg-white border-b px-4 py-2" aria-label="Cola de impresión">
    <div className="flex flex-wrap gap-2 items-center">
      <button onClick={() => setOpen(v => !v)} className="text-sm font-semibold">Cola de impresión ({queue.length}/100)</button>
      <select aria-label="Formato de impresión" value={format} disabled={busy} onChange={e => setFormat(e.target.value)} className="border rounded p-1 text-sm"><option value="a4">A4</option><option value="thermal">Térmica 80 mm</option></select>
      <button disabled={!queue.length || busy} onClick={() => prepare(queue)} className="px-3 py-1 rounded bg-brand-primary text-white disabled:opacity-40">{busy ? 'Preparando…' : 'Imprimir cola'}</button>
      <button disabled={!queue.length || busy} onClick={() => setQueue([])} className="text-sm disabled:opacity-40">Vaciar</button>
      <span className="text-xs text-gray-500">Personal y temporal; se borra al recargar.</span>
    </div>
    {error && <p role="alert" className="text-sm text-red-600 mt-2">{error}</p>}
    {open && <ul className="max-h-44 overflow-auto text-sm mt-2">{queue.map(p => <li key={deliveryKey(p)} className="flex items-center gap-2 py-1"><span>{deliveryKey(p)} · {p.recipient_name}</span><button aria-label={`Quitar ${deliveryKey(p)}`} onClick={() => setQueue(q => q.filter(row => deliveryKey(row) !== deliveryKey(p)))}><X size={14}/></button></li>)}</ul>}
    <div style={{ display: 'none' }}><div ref={ref}>{format === 'a4' ? <A4PrintLayout limaDeliveries={lima} provinciaDeliveries={provincia}/> : <ThermalPrintLayout limaDeliveries={lima} provinciaDeliveries={provincia}/>}</div></div>
  </section>;
}
